import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { libraryPeopleQueryKeys } from '@/hooks/library/types'
import { markEntitiesAsConfirmedInRelationships } from '@/hooks/library/useEntityRelationships'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityPerson } from '@/types/library/Entity'

interface ConfirmPeoplePayload {
  personIds: UUID[]
}

const confirmPeople = async (
  orgId: UUID,
  payload: ConfirmPeoplePayload,
  accessToken: string,
) => {
  for (const personId of payload.personIds) {
    const response = await fetch(
      `${API_URL}/api/v1/organizations/${orgId}/entities/${personId}/confirm`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    if (!response.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: response.statusText,
        cause: response,
      })
    }
  }
}

interface UseConfirmPeopleArgs {
  orgId: UUID | ''
  optimisticUpdate?: boolean
}

export const useConfirmPeople = ({
  orgId,
  optimisticUpdate = false,
}: UseConfirmPeopleArgs) => {
  const {
    accessToken,
    user: { userId },
  } = useAuthInfoWithThrow()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: ConfirmPeoplePayload) => {
      if (!orgId) {
        throw new Error('orgId is required')
      }
      if (!accessToken) {
        throw new Error('Missing access token')
      }
      return confirmPeople(orgId, payload, accessToken)
    },
    onMutate: async (payload) => {
      if (!orgId) return
      if (!optimisticUpdate) return
      const key = libraryPeopleQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key })
      const previousData =
        queryClient.getQueryData<PaginatedResponse<EntityPerson>>(key)
      if (previousData) {
        const updatedData = previousData.data.map((p) => {
          if (payload.personIds.includes(p.id)) {
            return {
              ...p,
              confirmed: true,
              confirmed_by_user_id: userId as UUID,
            }
          }
          return p
        })
        queryClient.setQueryData<PaginatedResponse<EntityPerson>>(key, {
          ...previousData,
          data: updatedData,
        })
      }
      return { previousData }
    },
    onSuccess: (data, payload) => {
      if (!orgId) return

      // Mark confirmed entities in entity relationship queries
      markEntitiesAsConfirmedInRelationships(
        queryClient,
        payload.personIds,
        userId as UUID,
      )

      return queryClient.invalidateQueries({
        queryKey: libraryPeopleQueryKeys.organization({ orgId }),
      })
    },
  })
}
